import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";

const API_URL = "https://api.wifikamu.my.id";

const initialForm = {
  // ==========================================
  // PPP SECRET
  // ==========================================
  name: "",
  password: "",
  service: "pppoe",
  profile: "",
  caller_id: "",
  routes: "",
  ipv6_routes: "",
  comment: "",
  local_address: "",
  remote_address: "",

  // ==========================================
  // CUSTOMER ACTIVATION
  // ==========================================
  tgl_aktivasi: "",
  customer_id: "",
  fullname: "",
  alamat: "",
  kelurahan: "",
  kecamatan: "",
  kab_kota: "",
  telp: "",
  email: "",
  kordinat: "",
  paket_bandwidth: "",
  marketing_group_id: "",
  bts_note: "",
  client_note: "",
  additional_info_a: "",
  additional_info_b: "",
  additional_info_c: "",
  referral_code: "",
};

const AddPppSecretModal = ({ isOpen, onClose, onSuccess }) => {
  const [searchParams] = useSearchParams();

  const usermanId = searchParams.get("userman_id");
  const deviceName = searchParams.get("device_name");

  const [loadingProfiles, setLoadingProfiles] = useState(false);
  const [loadingMarketing, setLoadingMarketing] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [profiles, setProfiles] = useState([]);
  const [marketingUsers, setMarketingUsers] = useState([]);

  const [form, setForm] = useState(initialForm);

  // =========================================================
  // LOAD DATA
  // =========================================================

  useEffect(() => {
    if (isOpen && usermanId) {
      loadProfiles();
      loadMarketingUsers();

      // Default tanggal aktivasi = hari ini
      setForm((prev) => ({
        ...prev,
        tgl_aktivasi:
          prev.tgl_aktivasi || new Date().toISOString().split("T")[0],
      }));
    }
  }, [isOpen, usermanId]);

  // =========================================================
  // LOAD PPP PROFILE
  // =========================================================

  const loadProfiles = async () => {
    if (!usermanId) {
      console.error("userman_id tidak ditemukan di URL");
      return;
    }

    try {
      setLoadingProfiles(true);

      const token = localStorage.getItem("token");

      const response = await axios.get(
        `https://api.wifikamu.my.id/ppp-profile-by-device/${usermanId}`,
        {
          headers: {
            Authorization: token,
          },
        }
      );

      setProfiles(response.data?.data || []);
    } catch (error) {
      console.error("Load PPP Profile Error:", error);

      setProfiles([]);

      alert(error?.response?.data?.message || "Gagal mengambil PPP Profile");
    } finally {
      setLoadingProfiles(false);
    }
  };

  // =========================================================
  // LOAD MARKETING USERS
  // =========================================================

  const loadMarketingUsers = async () => {
    try {
      setLoadingMarketing(true);

      const token = localStorage.getItem("token");

      const response = await axios.get(`${API_URL}/users`, {
        headers: {
          Authorization: token,
        },
      });

      setMarketingUsers(response.data.data || []);
    } catch (error) {
      console.error("Load Marketing Users Error:", error);
    } finally {
      setLoadingMarketing(false);
    }
  };

  // =========================================================
  // HANDLE CHANGE
  // =========================================================

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // =========================================================
  // SUBMIT
  // =========================================================

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setSubmitting(true);

      const token = localStorage.getItem("token");

      const payload = {
        // ==========================================
        // PPP SECRET
        // ==========================================
        userman_id: usermanId,
        name: form.name,
        password: form.password,
        service: form.service,
        profile: form.profile || null,
        caller_id: form.caller_id || null,
        routes: form.routes || null,
        ipv6_routes: form.ipv6_routes || null,
        comment: form.comment || null,
        local_address: form.local_address || null,
        remote_address: form.remote_address || null,

        // ==========================================
        // CUSTOMER ACTIVATION
        // ==========================================
        tgl_aktivasi: form.tgl_aktivasi || null,
        customer_id: form.customer_id || null,
        fullname: form.fullname || null,
        alamat: form.alamat || null,
        kelurahan: form.kelurahan || null,
        kecamatan: form.kecamatan || null,
        kab_kota: form.kab_kota || null,
        telp: form.telp || null,
        email: form.email || null,
        kordinat: form.kordinat || null,
        paket_bandwidth: form.paket_bandwidth || null,

        marketing_group_id: form.marketing_group_id || null,

        bts_note: form.bts_note || null,
        client_note: form.client_note || null,

        additional_info_a: form.additional_info_a || null,

        additional_info_b: form.additional_info_b || null,

        additional_info_c: form.additional_info_c || null,

        referral_code: form.referral_code || null,
      };

      const response = await axios.post(`${API_URL}/ppp-secret/add`, payload, {
        headers: {
          Authorization: token,
        },
      });

      if (onSuccess) {
        onSuccess(response.data);
      }

      // Reset form
      setForm({
        ...initialForm,
        tgl_aktivasi: new Date().toISOString().split("T")[0],
      });

      onClose();
    } catch (error) {
      console.error("Add PPP Secret Error:", error);

      alert(error?.response?.data?.message || "Gagal membuat PPP Secret");
    } finally {
      setSubmitting(false);
    }
  };

  // =========================================================
  // CLOSE
  // =========================================================

  const handleClose = () => {
    if (submitting) return;

    setForm(initialForm);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="flex max-h-[95vh] w-full max-w-5xl flex-col overflow-hidden rounded-xl bg-white shadow-xl dark:bg-slate-900">
        {/* =====================================================
            HEADER
        ====================================================== */}

        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4 dark:border-slate-700">
          <div>
            <h2 className="text-lg font-semibold text-slate-800 dark:text-white">
              Add PPP Secret
            </h2>

            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              Tambahkan PPP Secret sekaligus data aktivasi pelanggan
            </p>
          </div>

          <button
            type="button"
            onClick={handleClose}
            disabled={submitting}
            className="text-xl text-slate-500 hover:text-red-500 disabled:opacity-50"
          >
            ✕
          </button>
        </div>

        {/* =====================================================
            FORM
        ====================================================== */}

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="flex-1 overflow-y-auto p-6">
            {/* =================================================
                SECTION 1
            ================================================== */}

            <div className="mb-8">
              <div className="mb-5 border-b border-slate-200 pb-3 dark:border-slate-700">
                <h3 className="text-base font-semibold text-slate-800 dark:text-white">
                  1. PPP Secret
                </h3>

                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  Data PPP Secret yang akan dibuat pada MikroTik.
                </p>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {/* Username */}

                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
                    Username
                  </label>

                  <input
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    required
                    placeholder="Username PPP Secret"
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500 dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                  />
                </div>

                {/* Password */}

                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
                    Password
                  </label>

                  <input
                    type="text"
                    name="password"
                    value={form.password}
                    onChange={handleChange}
                    required
                    placeholder="Password PPP Secret"
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500 dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                  />
                </div>

                {/* Service */}

                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
                    Service
                  </label>

                  <select
                    name="service"
                    value={form.service}
                    onChange={handleChange}
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                  >
                    <option value="pppoe">PPPoE</option>

                    <option value="any">Any</option>

                    <option value="ovpn">OVPN</option>

                    <option value="pptp">PPTP</option>

                    <option value="l2tp">L2TP</option>

                    <option value="sstp">SSTP</option>
                  </select>
                </div>

                {/* Profile */}

                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
                    Profile
                  </label>

                  <select
                    name="profile"
                    value={form.profile}
                    onChange={handleChange}
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                  >
                    <option value="">
                      {loadingProfiles
                        ? "Loading profile..."
                        : "Select Profile"}
                    </option>

                    {profiles.map((profile) => (
                      <option key={profile.id} value={profile.name}>
                        {profile.name}
                        {profile.rate_limit ? ` (${profile.rate_limit})` : ""}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Local Address */}

                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
                    Local Address
                  </label>

                  <input
                    type="text"
                    name="local_address"
                    value={form.local_address}
                    onChange={handleChange}
                    placeholder="Local Address"
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500 dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                  />
                </div>

                {/* Remote Address */}

                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
                    Remote Address
                  </label>

                  <input
                    type="text"
                    name="remote_address"
                    value={form.remote_address}
                    onChange={handleChange}
                    placeholder="Remote Address"
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500 dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                  />
                </div>

                {/* Customer ID */}

                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
                    Customer ID
                  </label>

                  <input
                    type="text"
                    name="customer_id"
                    value={form.customer_id}
                    onChange={handleChange}
                    placeholder="Customer ID"
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500 dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                  />
                </div>

                {/* Comment */}

                <div className="md:col-span-2">
                  <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
                    Comment
                  </label>

                  <textarea
                    rows="3"
                    name="comment"
                    value={form.comment}
                    onChange={handleChange}
                    placeholder="Comment PPP Secret"
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500 dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                  />
                </div>
              </div>
            </div>

            {/* =================================================
                SECTION 2
            ================================================== */}

            <div>
              <div className="mb-5 border-b border-slate-200 pb-3 dark:border-slate-700">
                <h3 className="text-base font-semibold text-slate-800 dark:text-white">
                  2. Data Aktivasi Pelanggan
                </h3>

                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  Informasi pelanggan dan data instalasi. BTS otomatis mengikuti
                  Userman ID.
                </p>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {/* Tanggal Aktivasi */}

                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
                    Tanggal Aktivasi
                  </label>

                  <input
                    type="date"
                    name="tgl_aktivasi"
                    value={form.tgl_aktivasi}
                    onChange={handleChange}
                    required
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500 dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                  />
                </div>

                {/* Customer ID */}

                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
                    Customer ID
                  </label>

                  <input
                    type="text"
                    name="customer_id"
                    value={form.customer_id}
                    onChange={handleChange}
                    placeholder="Customer ID"
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500 dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                  />
                </div>

                {/* Fullname */}

                <div className="md:col-span-2">
                  <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
                    Nama Lengkap
                  </label>

                  <input
                    type="text"
                    name="fullname"
                    value={form.fullname}
                    onChange={handleChange}
                    placeholder="Nama lengkap pelanggan"
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500 dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                  />
                </div>

                {/* Alamat */}

                <div className="md:col-span-2">
                  <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
                    Alamat
                  </label>

                  <textarea
                    rows="3"
                    name="alamat"
                    value={form.alamat}
                    onChange={handleChange}
                    placeholder="Alamat lengkap pelanggan"
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500 dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                  />
                </div>

                {/* Kelurahan */}

                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
                    Kelurahan
                  </label>

                  <input
                    type="text"
                    name="kelurahan"
                    value={form.kelurahan}
                    onChange={handleChange}
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500 dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                  />
                </div>

                {/* Kecamatan */}

                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
                    Kecamatan
                  </label>

                  <input
                    type="text"
                    name="kecamatan"
                    value={form.kecamatan}
                    onChange={handleChange}
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500 dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                  />
                </div>

                {/* Kab Kota */}

                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
                    Kabupaten / Kota
                  </label>

                  <input
                    type="text"
                    name="kab_kota"
                    value={form.kab_kota}
                    onChange={handleChange}
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500 dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                  />
                </div>

                {/* Telepon */}

                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
                    Telepon
                  </label>

                  <input
                    type="text"
                    name="telp"
                    value={form.telp}
                    onChange={handleChange}
                    placeholder="08xxxxxxxxxx"
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500 dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                  />
                </div>

                {/* Email */}

                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
                    Email
                  </label>

                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="customer@email.com"
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500 dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                  />
                </div>

                {/* Koordinat */}

                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
                    Koordinat
                  </label>

                  <input
                    type="text"
                    name="kordinat"
                    value={form.kordinat}
                    onChange={handleChange}
                    placeholder="-6.123456, 106.123456"
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500 dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                  />
                </div>

                {/* Paket Bandwidth */}

                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
                    Paket Bandwidth
                  </label>

                  <input
                    type="text"
                    name="paket_bandwidth"
                    value={form.paket_bandwidth}
                    onChange={handleChange}
                    placeholder="20 Mbps"
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500 dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                  />
                </div>

                {/* Marketing */}

                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
                    Marketing
                  </label>

                  <select
                    name="marketing_group_id"
                    value={form.marketing_group_id}
                    onChange={handleChange}
                    required
                    disabled={loadingMarketing}
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white disabled:opacity-50"
                  >
                    <option value="">
                      {loadingMarketing
                        ? "Loading marketing..."
                        : "Pilih Marketing"}
                    </option>

                    {marketingUsers.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.first_name} {user.last_name}
                      </option>
                    ))}
                  </select>

                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                    Nama marketing akan diambil otomatis oleh backend.
                  </p>
                </div>

                {/* BTS */}

                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
                    BTS
                  </label>

                  <input
                    type="text"
                    value={`Otomatis dari Userman ID: ${usermanId}`}
                    disabled
                    className="w-full rounded-lg border border-slate-200 bg-slate-100 px-3 py-2 text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400"
                  />

                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                    BTS tidak dapat diubah dari form ini.
                  </p>
                </div>

                {/* BTS Note */}

                <div className="md:col-span-2">
                  <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
                    BTS Note
                  </label>

                  <textarea
                    rows="2"
                    name="bts_note"
                    value={form.bts_note}
                    onChange={handleChange}
                    placeholder="Catatan BTS"
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500 dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                  />
                </div>

                {/* Client Note */}

                <div className="md:col-span-2">
                  <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
                    Client Note
                  </label>

                  <textarea
                    rows="2"
                    name="client_note"
                    value={form.client_note}
                    onChange={handleChange}
                    placeholder="Catatan pelanggan"
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500 dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                  />
                </div>

                {/* Additional Info A */}

                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
                    Additional Info A
                  </label>

                  <textarea
                    rows="2"
                    name="additional_info_a"
                    value={form.additional_info_a}
                    onChange={handleChange}
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500 dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                  />
                </div>

                {/* Additional Info B */}

                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
                    Additional Info B
                  </label>

                  <textarea
                    rows="2"
                    name="additional_info_b"
                    value={form.additional_info_b}
                    onChange={handleChange}
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500 dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                  />
                </div>

                {/* Additional Info C */}

                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
                    Additional Info C
                  </label>

                  <textarea
                    rows="2"
                    name="additional_info_c"
                    value={form.additional_info_c}
                    onChange={handleChange}
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500 dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                  />
                </div>

                {/* Referral Code */}

                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
                    Referral Code
                  </label>

                  <input
                    type="text"
                    name="referral_code"
                    value={form.referral_code}
                    onChange={handleChange}
                    placeholder="REF001"
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500 dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* ===================================================
              FOOTER
          ==================================================== */}

          <div className="flex shrink-0 justify-end gap-3 border-t border-slate-200 bg-white px-6 py-4 dark:border-slate-700 dark:bg-slate-900">
            <button
              type="button"
              onClick={handleClose}
              disabled={submitting}
              className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={submitting || loadingMarketing || loadingProfiles}
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {submitting ? "Creating..." : "Create PPP Secret & Aktivasi"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddPppSecretModal;
